import { type CSSProperties, type SVGAttributes } from 'react'
import { cn } from '@/lib/utils'

// Native logo dimensions the viewBox/wrapper widths below are derived from.
const NATIVE_HEIGHT = 32
const NATIVE_WIDTH = 148
const NATIVE_EXPANDED_WRAPPER_WIDTH = 160
const NATIVE_COLLAPSED_WRAPPER_WIDTH = 36

type BrandLogoProps = SVGAttributes<SVGElement> & {
  invert?: boolean
  grayscale?: boolean
  shadow?: boolean
  scrolled?: boolean
  // Collapse to icon-only below the `lg` breakpoint, independent of
  // `scrolled` (which is scroll-position-driven, for the main site nav) - for
  // placements like the traffic sidebar where the trigger is viewport width.
  // Pure CSS/no JS, and only takes effect when `scrolled` isn't set.
  collapseBelowLg?: boolean
  height?: number
}

export default function BrandLogo({
  invert,
  grayscale,
  shadow,
  scrolled,
  collapseBelowLg,
  height = NATIVE_HEIGHT,
  ...props
}: BrandLogoProps) {
  const fill = invert ? 'fill-white' : 'fill-dark-950 dark:fill-white'
  const scale = height / NATIVE_HEIGHT
  const width = Math.round(NATIVE_WIDTH * scale)
  const wrapperStyle = {
    '--brand-expanded-w': `${Math.round(NATIVE_EXPANDED_WRAPPER_WIDTH * scale)}px`,
    '--brand-collapsed-w': `${Math.round(NATIVE_COLLAPSED_WRAPPER_WIDTH * scale)}px`,
  } as CSSProperties

  return (
    <div
      className={cn(
        'meticulous-ignore -ml-1 overflow-x-clip pl-1 transition-[width,transform] ease-in-out',
        scrolled
          ? 'w-[var(--brand-collapsed-w)] delay-150 duration-300 md:w-[var(--brand-expanded-w)]'
          : collapseBelowLg
            ? 'w-[var(--brand-collapsed-w)] duration-150 lg:w-[var(--brand-expanded-w)]'
            : 'w-[var(--brand-expanded-w)] duration-150',
      )}
      style={wrapperStyle}
      data-review="ignore"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${NATIVE_WIDTH} ${NATIVE_HEIGHT}`}
        fill="none"
        overflow="visible"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <defs>
          <filter id="drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dx="0" dy="1" result="offsetblur" />
            <feFlood floodColor="white" floodOpacity="1" />
            <feComposite in2="offsetblur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={shadow ? 'url(#drop-shadow)' : undefined}>
          <path
            className={grayscale ? 'fill-white opacity-40' : 'fill-primary-100'}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.7322 17.2677C1.27981 16.8153 1 16.1903 1 15.5C1 14.1193 2.11929 13 3.5 13H17C18.1046 13 19 13.8954 19 15L19 15.0074V28.4917L19 28.5C19 29.8807 17.8807 31 16.5 31C15.8096 31 15.1846 30.7202 14.7322 30.2677L1.7322 17.2677Z"
          />
          <path
            className={grayscale ? 'fill-white opacity-70' : 'fill-primary-300'}
            d="M10 11C8.89543 11 8 10.1046 8 9C8 7.89543 8.89543 7 10 7H23C24.1046 7 25 7.89484 25 8.99941C25 12.6257 25 20.0896 25 22C25 23.1046 24.1046 24 23 24C21.8954 24 21 23.1046 21 22V15C21 12.7909 19.2091 11 17 11H10Z"
          />
          <path
            className={grayscale ? 'fill-white opacity-90' : 'fill-primary-500'}
            d="M17 5C15.8954 5 15 4.10457 15 3C15 1.89543 15.8954 1 17 1H29C30.1046 1 31 1.89119 31 2.99576C31 6.39383 31 13.1184 31 15C31 16.1046 30.1046 17 29 17C27.8954 17 27 16.1046 27 15V9C27 6.79086 25.2091 5 23 5H17Z"
          />
          <path
            className={fill}
            d="M118.948 21.7009C116.748 21.7009 115.449 20.4756 115.449 18.6272C115.449 16.758 116.853 15.595 119.262 15.408L122.488 15.1588V14.8681C122.488 13.165 121.461 12.5628 120.079 12.5628C118.424 12.5628 117.481 13.2897 117.481 14.515H115.763C115.763 12.3966 117.523 11.0051 120.163 11.0051C122.697 11.0051 124.415 12.3343 124.415 15.0758V21.4517H122.739L122.53 19.811C122.006 20.974 120.603 21.7009 118.948 21.7009ZM119.513 20.1848C121.378 20.1848 122.509 18.9802 122.509 17.0695V16.5295L119.89 16.7372C118.151 16.9034 117.439 17.568 117.439 18.5649C117.439 19.6448 118.235 20.1848 119.513 20.1848Z"
          />
          <path
            className={fill}
            d="M126.095 26V11.2959H127.876L128.022 13.1235C128.714 11.732 130.096 11.0051 131.73 11.0051C134.663 11.0051 136.486 13.1858 136.486 16.3011C136.486 19.3956 134.768 21.7217 131.73 21.7217C130.096 21.7217 128.735 21.0363 128.064 19.7902V26H126.095ZM128.085 16.3842C128.085 18.461 129.3 19.9564 131.311 19.9564C133.323 19.9564 134.517 18.461 134.517 16.3842C134.517 14.2865 133.323 12.7912 131.311 12.7912C129.3 12.7912 128.085 14.2658 128.085 16.3842Z"
          />
          <path
            className={fill}
            d="M137.609 26V11.2959H139.39L139.536 13.1235C140.228 11.732 141.61 11.0051 143.244 11.0051C146.177 11.0051 148 13.1858 148 16.3011C148 19.3956 146.282 21.7217 143.244 21.7217C141.61 21.7217 140.249 21.0363 139.578 19.7902V26H137.609ZM139.599 16.3842C139.599 18.461 140.814 19.9564 142.825 19.9564C144.837 19.9564 146.031 18.461 146.031 16.3842C146.031 14.2865 144.837 12.7912 142.825 12.7912C140.814 12.7912 139.599 14.2658 139.599 16.3842Z"
          />
          <path
            className={fill}
            d="M95.2831 21.4518H97.8389V15.9273C97.8389 14.2658 98.8026 13.1859 100.374 13.1859C101.694 13.1859 102.553 14.0166 102.553 15.7404V21.4518H105.108V15.1381C105.108 12.4797 103.768 10.8806 101.17 10.8806C99.7663 10.8806 98.5093 11.4829 97.8599 12.5213L97.6504 11.1921H95.2831V21.4518Z"
          />
          <path
            className={fill}
            d="M79.2947 7.59919C79.2947 8.4507 79.9861 9.13606 80.8659 9.13606C81.7039 9.13606 82.3953 8.4507 82.3953 7.59919C82.3953 6.74768 81.7039 6.08309 80.8659 6.08309C79.9861 6.08309 79.2947 6.74768 79.2947 7.59919Z"
          />
          <path className={fill} d="M79.588 11.1922V21.4518H82.1439V11.1922H79.588Z" />
          <path
            className={fill}
            d="M67.9872 16.3012C67.9872 13.1237 70.1031 10.8807 73.1617 10.8807C75.9899 10.8807 77.9173 12.4383 78.1896 14.9098H75.6338C75.3405 13.7467 74.4815 13.1444 73.2665 13.1444C71.6324 13.1444 70.543 14.3698 70.543 16.3012C70.543 18.2327 71.5486 19.4373 73.1827 19.4373C74.4606 19.4373 75.3614 18.8142 75.6338 17.6927H78.2105C77.8963 20.0811 75.8852 21.7218 73.1827 21.7218C70.0403 21.7218 67.9872 19.5619 67.9872 16.3012Z"
          />
          <path
            className={fill}
            d="M63.9861 13.3105H63.9911V21.4518H66.547V11.1921H63.9861V13.3105Z"
          />
          <path
            className={fill}
            d="M63.7607 7.5161C63.7607 8.36761 64.4101 9.03221 65.269 9.03221C66.128 9.03221 66.7984 8.36761 66.7984 7.5161C66.7984 6.66459 66.128 6 65.269 6C64.4101 6 63.7607 6.66459 63.7607 7.5161Z"
          />
          <path
            className={fill}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M39 16.3219C39 19.4995 41.1578 21.7217 44.2374 21.7217C46.9398 21.7217 48.8253 20.3718 49.2652 18.1288H46.8979C46.6047 19.1049 45.6829 19.6449 44.2793 19.6449C42.5824 19.6449 41.6187 18.7311 41.4301 16.9242L49.2233 16.9034V16.135C49.2233 12.9367 47.2541 10.8806 44.1536 10.8806C41.1159 10.8806 39 13.1028 39 16.3219ZM41.493 15.3043C41.7444 13.7674 42.6662 12.9574 44.1745 12.9574C45.7038 12.9574 46.6885 13.892 46.6885 15.3043H41.493Z"
          />
          <path
            className={fill}
            d="M50.6822 11.1921H50.6798V13.3105H50.6822V21.4518H53.2171V13.3105H55.7127V11.1921H53.2171V9.94602C53.2171 8.92836 53.5104 8.26377 54.8092 8.26377H55.71V6.10384C55.2701 6.02077 54.7883 6 54.3902 6C52.1486 6 50.6822 7.16304 50.6822 9.94602V11.1921Z"
          />
          <path
            className={fill}
            d="M57.4065 13.3105H57.4045V11.1921H57.4065V9.94602C57.4065 7.16304 58.8729 6 60.9679 6C61.3869 6 61.8478 6.02077 62.2877 6.10384V8.26377H61.4078C60.2556 8.26377 59.9414 8.92836 59.9414 9.94602V11.1921H62.2906V13.3105H59.9414V21.4518H57.4065V13.3105Z"
          />
          <path
            className={fill}
            d="M106.802 14.4113H106.8V16.5297H106.802V17.7758C106.802 20.5588 108.269 21.7218 110.363 21.7218C110.782 21.7218 111.243 21.7011 111.683 21.618V19.4581H110.803C109.651 19.4581 109.337 18.7935 109.337 17.7758V13.3059H111.686V11.1875H109.337V8.26406H106.802V14.4113Z"
          />
          <path
            className={fill}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M88.8276 21.7217C85.748 21.7217 83.5902 19.4995 83.5902 16.3219C83.5902 13.1028 85.7061 10.8806 88.7438 10.8806C91.8443 10.8806 93.8135 12.9367 93.8135 16.135V16.9034L86.0204 16.9242C86.2089 18.7311 87.1726 19.6449 88.8695 19.6449C90.2731 19.6449 91.1949 19.1049 91.4882 18.1288H93.8555C93.4155 20.3718 91.5301 21.7217 88.8276 21.7217ZM88.7647 12.9574C87.2564 12.9574 86.3346 13.7674 86.0832 15.3043H91.2787C91.2787 13.892 90.294 12.9574 88.7647 12.9574Z"
          />
        </g>
      </svg>
    </div>
  )
}

export function LogoText() {
  return (
    <svg
      width="102"
      height="22"
      viewBox="0 0 102 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.4">
        <path
          d="M74.3656 18.0594C72.3459 18.0594 71.1533 16.9361 71.1533 15.2418C71.1533 13.5284 72.4421 12.4622 74.6542 12.2909L77.6164 12.0624V11.7959C77.6164 10.2348 76.6739 9.68272 75.4043 9.68272C73.8847 9.68272 73.0191 10.349 73.0191 11.4723H71.4418C71.4418 9.53042 73.0576 8.25488 75.4813 8.25488C77.8088 8.25488 79.3861 9.4733 79.3861 11.9863V17.8309H77.8472L77.6549 16.3269C77.174 17.393 75.8852 18.0594 74.3656 18.0594ZM74.885 16.6696C76.5969 16.6696 77.6357 15.5654 77.6357 13.8139V13.3189L75.2312 13.5093C73.6347 13.6616 72.9807 14.2708 72.9807 15.1847C72.9807 16.1746 73.7116 16.6696 74.885 16.6696Z"
          fill="black"
        />
        <path
          d="M80.9284 22.0002V8.52141H82.5634L82.698 10.1967C83.3328 8.92121 84.6023 8.25488 86.1027 8.25488C88.7956 8.25488 90.4691 10.2539 90.4691 13.1095C90.4691 15.9462 88.8918 18.0784 86.1027 18.0784C84.6023 18.0784 83.352 17.4502 82.7365 16.3079V22.0002H80.9284ZM82.7557 13.1857C82.7557 15.0895 83.8714 16.4602 85.718 16.4602C87.5646 16.4602 88.661 15.0895 88.661 13.1857C88.661 11.2629 87.5646 9.89214 85.718 9.89214C83.8714 9.89214 82.7557 11.2438 82.7557 13.1857Z"
          fill="black"
        />
        <path
          d="M91.5003 22.0002V8.52141H93.1353L93.27 10.1967C93.9048 8.92121 95.1743 8.25488 96.6746 8.25488C99.3676 8.25488 101.041 10.2539 101.041 13.1095C101.041 15.9462 99.4638 18.0784 96.6746 18.0784C95.1743 18.0784 93.924 17.4502 93.3084 16.3079V22.0002H91.5003ZM93.3277 13.1857C93.3277 15.0895 94.4433 16.4602 96.2899 16.4602C98.1365 16.4602 99.233 15.0895 99.233 13.1857C99.233 11.2629 98.1365 9.89214 96.2899 9.89214C94.4433 9.89214 93.3277 11.2438 93.3277 13.1857Z"
          fill="black"
        />
      </g>
      <path
        d="M52.6371 17.8311H54.9838V12.767C54.9838 11.244 55.8686 10.254 57.3113 10.254C58.5231 10.254 59.3118 11.0156 59.3118 12.5957V17.8311H61.6585V12.0436C61.6585 9.60676 60.4274 8.14085 58.0422 8.14085C56.7535 8.14085 55.5993 8.69295 55.003 9.64484L54.8107 8.42642H52.6371V17.8311Z"
        fill="black"
      />
      <path
        d="M37.9569 5.13292C37.9569 5.91347 38.5916 6.54172 39.3995 6.54172C40.1689 6.54172 40.8037 5.91347 40.8037 5.13292C40.8037 4.35237 40.1689 3.74316 39.3995 3.74316C38.5916 3.74316 37.9569 4.35237 37.9569 5.13292Z"
        fill="black"
      />
      <path d="M38.2262 8.42646V17.8311H40.5729V8.42646H38.2262Z" fill="black" />
      <path
        d="M27.5745 13.1098C27.5745 10.197 29.5173 8.14093 32.3257 8.14093C34.9224 8.14093 36.6921 9.56877 36.9422 11.8343H34.5954C34.3261 10.7682 33.5375 10.2161 32.4218 10.2161C30.9215 10.2161 29.9212 11.3393 29.9212 13.1098C29.9212 14.8803 30.8445 15.9845 32.3449 15.9845C33.5183 15.9845 34.3454 15.4134 34.5954 14.3853H36.9614C36.6729 16.5747 34.8263 18.0787 32.3449 18.0787C29.4596 18.0787 27.5745 16.0987 27.5745 13.1098Z"
        fill="black"
      />
      <path d="M23.9007 10.3683H23.9054V17.8311H26.2521V8.42645H23.9007V10.3683Z" fill="black" />
      <path
        d="M23.6938 5.05675C23.6938 5.8373 24.2901 6.44651 25.0787 6.44651C25.8674 6.44651 26.4829 5.8373 26.4829 5.05675C26.4829 4.2762 25.8674 3.66699 25.0787 3.66699C24.2901 3.66699 23.6938 4.2762 23.6938 5.05675Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.958984 13.1288C0.958984 16.0415 2.94023 18.0786 5.76784 18.0786C8.24921 18.0786 9.98039 16.8411 10.3843 14.7851H8.21073C7.94144 15.6798 7.09508 16.1748 5.80631 16.1748C4.24824 16.1748 3.36341 15.3371 3.19029 13.6809L10.3459 13.6618V12.9574C10.3459 10.0256 8.53774 8.14085 5.6909 8.14085C2.90176 8.14085 0.958984 10.1779 0.958984 13.1288ZM3.248 12.1959C3.47882 10.7871 4.32518 10.0446 5.71013 10.0446C7.11432 10.0446 8.01838 10.9013 8.01838 12.1959H3.248Z"
        fill="black"
      />
      <path
        d="M11.6853 8.42645H11.6832V10.3683H11.6853V17.8311H14.0128V10.3683H16.3043V8.42645H14.0128V7.28418C14.0128 6.35133 14.2821 5.74211 15.4747 5.74211H16.3018V3.76218C15.8979 3.68603 15.4555 3.66699 15.09 3.66699C13.0318 3.66699 11.6853 4.73311 11.6853 7.28418V8.42645Z"
        fill="black"
      />
      <path
        d="M17.8595 10.3683H17.8577V8.42645H17.8595V7.28418C17.8595 4.73311 19.206 3.66699 21.1295 3.66699C21.5142 3.66699 21.9374 3.68603 22.3413 3.76218V5.74211H21.5335C20.4755 5.74211 20.187 6.35133 20.187 7.28418V8.42645H22.344V10.3683H20.187V17.8311H17.8595V10.3683Z"
        fill="black"
      />
      <path
        d="M63.2136 11.3774H63.2118V13.3192H63.2136V14.4615C63.2136 17.0126 64.5601 18.0787 66.4836 18.0787C66.8683 18.0787 67.2915 18.0596 67.6954 17.9835V16.0035H66.8876C65.8296 16.0035 65.5411 15.3943 65.5411 14.4615V10.364H67.6981V8.42218H65.5411V5.74238H63.2136V11.3774Z"
        fill="black"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M46.7098 18.0786C43.8822 18.0786 41.9009 16.0415 41.9009 13.1288C41.9009 10.1779 43.8437 8.14085 46.6328 8.14085C49.4797 8.14085 51.2878 10.0256 51.2878 12.9574V13.6618L44.1322 13.6809C44.3053 15.3371 45.1902 16.1748 46.7482 16.1748C48.037 16.1748 48.8834 15.6798 49.1527 14.7851H51.3263C50.9223 16.8411 49.1911 18.0786 46.7098 18.0786ZM46.6521 10.0446C45.2671 10.0446 44.4207 10.7871 44.1899 12.1959H48.9603C48.9603 10.9013 48.0562 10.0446 46.6521 10.0446Z"
        fill="black"
      />
    </svg>
  )
}
